/*
 * The router, built from the manifest rather than beside it.
 *
 * Every route TanStack Router knows about comes from ROUTES, so FE-02's gate reading the manifest
 * is reading the real routing table. A route added directly here would be invisible to the gate —
 * which is exactly why there is no way to add one directly here.
 *
 * Screens are `React.lazy`, so a tenant without a module never downloads its code. That is the
 * "lazy-loaded" half of FE-02, and it is a privacy property as much as a performance one: the
 * shape of a feature a tenant has not bought is not delivered to their browser.
 */

import { Suspense, lazy } from 'react';
import type { ComponentType } from 'react';
import { createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { Spinner } from '@indovation/design-system';
import { ROUTES } from '../routes.js';
import RootLayout from './root-layout.js';
import Guard from './guard.js';
import NotFound from './not-found.js';
import { ErrorScreen } from './error-screen.js';

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: ErrorScreen,
});

export function buildRouter() {
  const children = ROUTES.map((entry) => {
    const Screen = lazy(entry.load as () => Promise<{ default: ComponentType }>);

    return createRoute({
      getParentRoute: () => rootRoute,
      path: entry.path,
      // Declared in the manifest, not here — one routing table, which is what the FE-02 gate reads.
      ...(entry.validateSearch ? { validateSearch: entry.validateSearch } : {}),
      component: () => (
        <Guard entry={entry}>
          <Suspense fallback={<Spinner />}>
            <Screen />
          </Suspense>
        </Guard>
      ),
    });
  });

  return createRouter({
    routeTree: rootRoute.addChildren(children),
    defaultNotFoundComponent: NotFound,
    // Every route, without each one declaring it. A screen that throws now renders the error inside
    // the shell rather than taking the router down with it — which is what used to happen, because
    // there was no boundary anywhere in the app.
    defaultErrorComponent: ErrorScreen,
  });
}
